// src/components/AppointmentsReportScreen.jsx
// Relatório de Consultas premium.
// Rota: /reports/appointments
// Endpoint correto no backend atual: GET /psy/report/appointment?id=ID_USUARIO
// UI isolada em .appt-* para não mexer em .dash-* global.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Ban,
  CalendarDays,
  CheckCircle2,
  Info,
  MapPin,
  Menu,
  Printer,
  RefreshCw,
  Video,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/appointments-report.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const STATUS_FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "pendente", label: "Pendente" },
  { value: "confirmado", label: "Confirmado" },
  { value: "cancelado", label: "Cancelado" },
  { value: "baixado", label: "Baixado" },
];

const TYPE_FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "Online", label: "Online" },
  { value: "Presencial", label: "Presencial" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function buildQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (String(value).trim() === "") return;
    if (String(value).trim() === "todos") return;
    query.set(key, String(value));
  });
  return query.toString();
}

function getTimeBR(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.detail || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function getAppointmentHealth(total, online, presencial) {
  const totalNumber = safeNumber(total);
  const onlineNumber = safeNumber(online);
  const presencialNumber = safeNumber(presencial);

  if (totalNumber <= 0) {
    return {
      label: "Sem consultas",
      tone: "neutral",
      icon: "i",
      description: "Ainda não há consultas para leitura do período.",
    };
  }

  if (onlineNumber > presencialNumber) {
    return {
      label: "Predomínio online",
      tone: "blue",
      icon: "good",
      description: "A maior parte das consultas está sendo feita online.",
    };
  }

  if (presencialNumber > onlineNumber) {
    return {
      label: "Predomínio presencial",
      tone: "green",
      icon: "good",
      description: "A maior parte das consultas está sendo feita presencialmente.",
    };
  }

  return {
    label: "Equilibrado",
    tone: "warning",
    icon: "balanced",
    description: "Online e presencial estão equilibrados no período.",
  };
}

function HealthStatusIcon({ tone }) {
  const Icon =
    tone === "good"
      ? CheckCircle2
      : tone === "bad"
        ? AlertCircle
        : tone === "warning"
          ? AlertTriangle
          : Info;

  return <Icon size={22} aria-hidden="true" />;
}

function KpiCard({
  icon: Icon,
  label,
  value,
  description,
  tone = "blue",
}) {
  return (
    <article className={`appt-kpi appt-kpi--${tone}`}>
      <div className="appt-kpi__icon" aria-hidden="true">
        <Icon size={19} />
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function DonutChart({ online, presencial }) {
  const onlineNumber = safeNumber(online);
  const presencialNumber = safeNumber(presencial);
  const total = onlineNumber + presencialNumber;

  const onlinePercent = total > 0 ? (onlineNumber / total) * 100 : 0;
  const presencialPercent = total > 0 ? (presencialNumber / total) * 100 : 0;

  return (
    <div className="appt-pie">
      <div className="appt-donut" role="img" aria-label="Distribuição de consultas">
        <div
          className="appt-donut__ring"
          style={{
            background: total
              ? `conic-gradient(var(--appt-blue-500) 0 ${onlinePercent}%, var(--appt-green-500) ${onlinePercent}% 100%)`
              : "conic-gradient(var(--appt-slate-200) 0 100%)",
          }}
        />

        <div className="appt-donut__hole">
          <strong>{total}</strong>
          <span>Online + Presencial</span>
        </div>
      </div>

      <div className="appt-legend">
        <LegendRow
          color="blue"
          label="Online"
          value={onlineNumber}
          percent={`${percentBR(onlinePercent)}%`}
        />

        <LegendRow
          color="green"
          label="Presencial"
          value={presencialNumber}
          percent={`${percentBR(presencialPercent)}%`}
        />
      </div>
    </div>
  );
}

function LegendRow({ color, label, value, percent }) {
  return (
    <div className="appt-legend__row">
      <span className={`appt-dot appt-dot--${color}`} aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        <small>{percent}</small>
      </div>
      <span>{value}</span>
    </div>
  );
}

function WeekChart({ values = [] }) {
  const rows = DAYS.map((label, index) => ({
    label,
    value: safeNumber(values?.[index]),
  }));

  const max = Math.max(1, ...rows.map((item) => item.value));

  if (!rows.some((item) => item.value > 0)) {
    return (
      <div className="appt-chart-empty">
        <span><CalendarDays size={24} /></span>
        <strong>Sem evolução no período.</strong>
        <small>Quando houver consultas, o gráfico de segunda a sexta aparecerá aqui.</small>
      </div>
    );
  }

  return (
    <div className="appt-bars" role="img" aria-label="Evolução de consultas por dia útil">
      <div className="appt-bars__y" aria-hidden="true">
        <span>{max}</span>
        <span>{Math.round(max / 2)}</span>
        <span>0</span>
      </div>

      <div className="appt-bars__plot">
        {rows.map((item) => {
          const height = Math.max(8, Math.round((item.value / max) * 138));

          return (
            <div className="appt-bars__col" key={item.label} title={`${item.label}: ${item.value}`}>
              <div className="appt-bars__bar-wrap">
                <span className="appt-bars__bar" style={{ height: `${height}px` }} />
              </div>

              <span className="appt-bars__label">{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AppointmentSkeleton() {
  return (
    <div className="appt-skeleton" aria-hidden="true">
      <div className="appt-skeleton__hero" />
      <div className="appt-skeleton__kpis">
        <span />
        <span />
        <span />
      </div>
      <div className="appt-skeleton__grid">
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  message,
  onRetry,
  onBack,
  isError = false,
}) {
  return (
    <section
      className={`appt-state ${isError ? "appt-state--error" : ""}`}
    >
      <div className="appt-state__icon" aria-hidden="true">
        {isError ? (
          <AlertCircle size={24} />
        ) : (
          <CalendarDays size={24} />
        )}
      </div>

      <strong>{title}</strong>
      <span>{message}</span>

      <div className="appt-state__actions">
        <button
          className="appt-btn appt-btn--ghost-dark"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          Indicadores
        </button>

        <button
          className="appt-btn appt-btn--primary"
          type="button"
          onClick={onRetry}
        >
          <RefreshCw size={16} />
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

export default function AppointmentsReportScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [typeFilter, setTypeFilter] = useState("todos");

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const loadReport = useCallback(async ({ signal, silent = false } = {}) => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!userId) {
      setError("ID do usuário não encontrado. Faça login novamente.");
      setLoading(false);
      return;
    }

    if (!token) {
      setError("Token não encontrado. Faça login novamente.");
      setLoading(false);
      return;
    }

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const params = buildQuery({
        id: userId,
        dataInicio,
        dataFim,
        status: statusFilter,
        tipoSessao: typeFilter,
      });

      const response = await fetch(
        `${API_BASE}/psy/report/appointment?${params}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        setError("Sessão expirada. Faça login novamente.");
        setReport(null);
        return;
      }

      if (!response.ok) {
        const message = await readApiError(response);
        setError(`Erro ao buscar relatório (${response.status}). ${message}`);
        setReport(null);
        return;
      }

      const data = await response.json().catch(() => null);

      setReport(data || null);
      setLastUpdatedAt(new Date());
    } catch (err) {
      if (err?.name === "AbortError") return;

      console.error("Erro ao buscar relatório de consultas:", err);
      setError("Erro de conexão com a API.");
      setReport(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dataFim, dataInicio, statusFilter, typeFilter]);

  useEffect(() => {
    const controller = new AbortController();
    loadReport({ signal: controller.signal });

    return () => controller.abort();
  }, [loadReport]);

  const total = safeNumber(report?.resumo?.total);
  const online = safeNumber(report?.resumo?.online);
  const presencial = safeNumber(report?.resumo?.presencial);
  const evolucao = Array.isArray(report?.evolucao)
    ? report.evolucao
    : Array.isArray(report?.distribuicao?.semana)
      ? report.distribuicao.semana
      : report?.distribuicao?.semana
        ? [
            report.distribuicao.semana.segunda,
            report.distribuicao.semana.terca,
            report.distribuicao.semana.quarta,
            report.distribuicao.semana.quinta,
            report.distribuicao.semana.sexta,
            report.distribuicao.semana.sabado,
            report.distribuicao.semana.domingo,
          ].map((value) => safeNumber(value))
        : [];

  const distributionTotal = online + presencial;
  const onlinePercent = distributionTotal > 0 ? (online / distributionTotal) * 100 : 0;
  const presencialPercent = distributionTotal > 0 ? (presencial / distributionTotal) * 100 : 0;

  const health = useMemo(
    () => getAppointmentHealth(total, online, presencial),
    [total, online, presencial]
  );

  const reload = () => loadReport({ silent: true });
  const clearManagementFilters = () => {
    setDataInicio("");
    setDataFim("");
    setStatusFilter("todos");
    setTypeFilter("todos");
  };
  const goBack = () => navigate("/reports");
  const goSchedule = () => navigate("/schedule");
  const goLogin = () => navigate("/login");
  const printReport = () => window.print();

  return (
    <div className="appt-page">
      <div className="appt-bg" aria-hidden="true">
        <span className="appt-bg__orb appt-bg__orb--one" />
        <span className="appt-bg__orb appt-bg__orb--two" />
        <span className="appt-bg__orb appt-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="appt-topbar">
        <button
          className="appt-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="appt-brand">
          <img src={Logo} alt="PsyAssist" className="appt-brand__logo" />
          <div className="appt-brand__text">
            <strong>Relatório de Consultas</strong>
            <span>Resumo, distribuição e evolução • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="appt-top-actions">
          <button
            className="appt-btn appt-btn--ghost appt-btn--desktop"
            type="button"
            onClick={goBack}
          >
            <ArrowLeft size={16} />
            Indicadores
          </button>

          <button
            className="appt-btn appt-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || Boolean(error) || !report}
          >
            <Printer size={16} />
            Imprimir
          </button>

          <button
            className="appt-btn appt-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            <RefreshCw
              size={16}
              className={refreshing ? "report-spin" : ""}
              aria-hidden="true"
            />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="appt-main">
        {loading ? (
          <AppointmentSkeleton />
        ) : error ? (
          <EmptyState
            title="Não foi possível carregar as consultas."
            message={error}
            onRetry={String(error).includes("Sessão") ? goLogin : reload}
            onBack={goBack}
            isError
          />
        ) : !report ? (
          <EmptyState
            title="Nenhum dado encontrado."
            message="Ainda não há consultas suficientes para montar este relatório."
            onRetry={reload}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="appt-hero">
              <div>
                <span className="appt-kicker">
                  <CalendarDays size={14} />
                  Agenda clínica
                </span>
                <h1>Consultas</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe o total de consultas, distribuição por modalidade e evolução
                  de segunda a sexta.
                </p>
              </div>

              <aside className={`appt-hero-card appt-hero-card--${health.tone}`}>
                <div className="appt-hero-card__icon" aria-hidden="true">
                  <HealthStatusIcon tone={health.tone} />
                </div>

                <div>
                  <small>Status do período</small>
                  <strong>{health.label}</strong>
                  <span>{health.description}</span>
                </div>
              </aside>
            </section>

            <section className="appt-panel appt-management-filters">
              <div className="appt-panel__head">
                <div>
                  <span>Filtros de gestão</span>
                  <h2>Refine a agenda</h2>
                  <p>Filtre por período, status e modalidade para analisar a operação da agenda.</p>
                </div>
              </div>

              <div className="appt-filter-grid">
                <label className="appt-filter-field">
                  <span>Data início</span>
                  <input type="date" value={dataInicio} onChange={(event) => setDataInicio(event.target.value)} />
                </label>
                <label className="appt-filter-field">
                  <span>Data fim</span>
                  <input type="date" value={dataFim} onChange={(event) => setDataFim(event.target.value)} />
                </label>
                <label className="appt-filter-field">
                  <span>Status</span>
                  <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    {STATUS_FILTERS.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                  </select>
                </label>
                <label className="appt-filter-field">
                  <span>Modalidade</span>
                  <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                    {TYPE_FILTERS.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                  </select>
                </label>
                <div className="appt-filter-actions">
                  <button className="appt-btn appt-btn--ghost-dark" type="button" onClick={clearManagementFilters}>Limpar</button>
                  <button className="appt-btn appt-btn--primary" type="button" onClick={reload} disabled={refreshing}>Aplicar</button>
                </div>
              </div>
            </section>

            <section className="appt-kpis">
              <KpiCard
                icon={CalendarDays}
                label="Total"
                value={total}
                description="Consultas registradas"
                tone="blue"
              />

              <KpiCard
                icon={Video}
                label="Online"
                value={online}
                description={`${percentBR(onlinePercent)}% da distribuição`}
                tone="green"
              />

              <KpiCard
                icon={MapPin}
                label="Presencial"
                value={presencial}
                description={`${percentBR(presencialPercent)}% da distribuição`}
                tone="amber"
              />

              <KpiCard
                icon={Ban}
                label="Cancelados"
                value={safeNumber(report?.resumo?.cancelado)}
                description={`${percentBR(report?.resumo?.taxaCancelamento)}% de cancelamento`}
                tone="red"
              />
            </section>

            <section className="appt-grid2">
              <div className="appt-panel">
                <div className="appt-panel__head">
                  <div>
                    <span>Distribuição</span>
                    <h2>Online × presencial</h2>
                    <p>Proporção das consultas por modalidade.</p>
                  </div>
                </div>

                <DonutChart online={online} presencial={presencial} />
              </div>

              <div className="appt-panel">
                <div className="appt-panel__head">
                  <div>
                    <span>Evolução</span>
                    <h2>Evolução das consultas</h2>
                    <p>Volume distribuído de segunda a sexta.</p>
                  </div>

                  {lastUpdatedAt ? (
                    <div className="appt-updated">
                      Atualizado às {getTimeBR(lastUpdatedAt)}
                    </div>
                  ) : null}
                </div>

                <WeekChart values={evolucao} />
              </div>
            </section>

            <section className="appt-bottom-actions">
              <button className="appt-btn appt-btn--ghost-dark" type="button" onClick={goBack}>
                <ArrowLeft size={16} />
                Central de indicadores
              </button>

              <button className="appt-btn appt-btn--primary" type="button" onClick={goSchedule}>
                <CalendarDays size={16} />
                Abrir agenda
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
